import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { getIdToken } from '../attendedUtils';
import { normalizeStr, Pagination, ShowsByYearTab, VenuesVisitedTab, ArtistsWatchedTab, TopCitiesTab, ShowsMapTab } from './ShowStatsTabs';
import { FollowersFollowingTab } from './FollowersFollowingTab';

const TABS = ['Show History', 'Shows by Year', 'Venues Visited', 'Artists Watched', 'Top Cities', 'Shows Map', 'Followers'];
const PAGE_SIZE = 25;

// ── Show History Tab ──────────────────────────────────────────────────────────
function ShowHistoryTab({ shows }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded yet. Start marking events as Attended!</div>;

    const q = normalizeStr(search);
    const filtered = search ? shows.filter(s =>
        [s.event_date ? new Date(s.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '',
         s.city, s.state, s.country, s.venue_name, s.artists, s.festival_name, s.role]
            .some(v => v && normalizeStr(v).includes(q))
    ) : shows;

    const totalShows = shows.length;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input
                type="text"
                placeholder="Search by date, city, venue, artists, role..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No shows match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>#</th>
                                    <th style={styles.th}>Role</th>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Venue</th>
                                    <th style={styles.th}>Artists</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((show, idx) => {
                                    const globalIdx = shows.indexOf(show);
                                    const showNum = totalShows - globalIdx;
                                    const dateObj = show.event_date ? new Date(show.event_date) : null;
                                    const formattedDate = dateObj
                                        ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                                        : '—';
                                    const isPerformed = show.role === 'Performed';
                                    const isWorked = show.role === 'Worked';
                                    return (
                                        <tr key={show.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                <Link to={`/events/${show.event_slug || show.event_id}`} style={styles.tableLink}>{showNum}</Link>
                                            </td>
                                            <td style={styles.td}>
                                                <span style={isPerformed ? styles.rolePerformed : isWorked ? styles.roleWorked : styles.roleAttended}>
                                                    {show.role}
                                                </span>
                                            </td>
                                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{formattedDate}</td>
                                            <td style={styles.td}>{show.city || '—'}</td>
                                            <td style={styles.td}>{show.state || '—'}</td>
                                            <td style={styles.td}>{show.country || '—'}</td>
                                            <td style={styles.td}>
                                                {show.venue_id
                                                    ? <Link to={`/venues/${show.venue_slug || show.venue_id}`} style={styles.tableLink}>{show.venue_name}</Link>
                                                    : show.venue_name || '—'}
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                {show.festival_name && show.festival_slug
                                                    ? <><Link to={`/festivals/${show.festival_slug}`} style={{ color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' }}>{show.festival_name}</Link>{show.artists ? ` - ${show.artists}` : ''}</>
                                                    : show.artists || '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── MyShows ───────────────────────────────────────────────────────────────────
function MyShows() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        document.title = 'SHARC - My Shows';
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, []);

    useEffect(() => {
        async function load() {
            try {
                const token = await getIdToken();
                const res = await axios.get(`${config.API_URL}/user/shows`, {
                    headers: { Authorization: token }
                });
                setData(res.data);
                setLoading(false);
            } catch (e) {
                setError('Failed to load your shows.');
                setLoading(false);
            }
        }
        load();
    }, []);

    if (loading) return <div style={styles.message}>Loading your shows...</div>;
    if (error) return <div style={styles.errorMsg}>{error}</div>;
    if (!data) return null;

    const { user, stats, show_history, venues_visited, artists_watched, geography, show_map, followers, following } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
    };

    const locationStr = [user.display_name && user.city ? user.city : null, user.state, user.country]
        .filter(Boolean).join(', ');

    return (
        <div>
            {/* Nav */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Link to="/" style={styles.backLink}>← Home</Link>
                <Link to="/profile" style={styles.editProfileLink}>Edit Profile</Link>
            </div>

            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerInitials}>
                    {user.display_name
                        ? user.display_name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2)
                        : '?'}
                </div>
                <div style={styles.headerText}>
                    <h1 style={styles.title}>{user.display_name || 'My Shows'}</h1>
                    {locationStr && <div style={styles.locationStr}>{locationStr}</div>}
                    {user.member_name && (
                        <div style={styles.memberBadge}>
                            Member of <Link to={`/members/${user.member_slug}`} style={styles.memberLink}>{user.member_name}</Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.total_shows}</div>
                    <div style={styles.statLabel}>Total Shows</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.attended_shows}</div>
                    <div style={styles.statLabel}>Attended</div>
                </div>
                {stats.performed_shows > 0 && (
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{stats.performed_shows}</div>
                        <div style={styles.statLabel}>Performed</div>
                    </div>
                )}
                {stats.worked_shows > 0 && (
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{stats.worked_shows}</div>
                        <div style={styles.statLabel}>Worked</div>
                    </div>
                )}
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{venues_visited.length}</div>
                    <div style={styles.statLabel}>Venues Visited</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.first_show_date ? formatDate(stats.first_show_date) : '—'}</div>
                    <div style={styles.statLabel}>First Show</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.last_show_date ? formatDate(stats.last_show_date) : '—'}</div>
                    <div style={styles.statLabel}>Most Recent Show</div>
                </div>
            </div>

            {/* Tabs */}
            <div style={styles.tabsWrapper}>
                <div style={styles.tabBar}>
                    {TABS.map((tab, i) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(i)}
                            style={{ ...styles.tabBtn, ...(activeTab === i ? styles.tabBtnActive : {}) }}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
                <div style={styles.tabContent}>
                    {activeTab === 0 && <ShowHistoryTab shows={show_history} />}
                    {activeTab === 1 && <ShowsByYearTab shows={show_history} />}
                    {activeTab === 2 && <VenuesVisitedTab venues={venues_visited} />}
                    {activeTab === 3 && <ArtistsWatchedTab artists={artists_watched} />}
                    {activeTab === 4 && <TopCitiesTab geography={geography} />}
                    {activeTab === 5 && <ShowsMapTab showMap={show_map} />}
                    {activeTab === 6 && <FollowersFollowingTab followers={followers} following={following} />}
                </div>
            </div>
        </div>
    );
}

const styles = {
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px' },
    editProfileLink: { color: '#aaa', textDecoration: 'none', fontSize: '13px', border: '1px solid #444', borderRadius: '4px', padding: '5px 12px' },
    header: { display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '32px' },
    headerInitials: { width: '64px', height: '64px', borderRadius: '10px', backgroundColor: '#bb86fc', color: '#121212', fontSize: '24px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, letterSpacing: '1px' },
    headerText: { flex: 1 },
    title: { fontSize: '36px', fontWeight: 'bold', color: '#e0e0e0', margin: '0 0 4px 0' },
    locationStr: { fontSize: '15px', color: '#aaa', marginBottom: '4px' },
    memberBadge: { fontSize: '13px', color: '#888' },
    memberLink: { color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px', marginBottom: '40px' },
    statCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px', textAlign: 'center' },
    statValue: { fontSize: '22px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    statLabel: { fontSize: '12px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },
    tabsWrapper: { marginTop: '8px' },
    tabBar: { display: 'flex', flexWrap: 'wrap', borderBottom: '2px solid #2a2a2a', gap: '2px' },
    tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-2px', padding: '10px 16px', fontSize: '13px', fontWeight: '600', color: '#aaa', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
    tabBtnActive: { color: '#bb86fc', borderBottomColor: '#bb86fc' },
    tabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '24px', minHeight: '100px' },
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '10px 16px', color: '#e0e0e0', verticalAlign: 'middle' },
    trEven: { backgroundColor: 'transparent' },
    trOdd: { backgroundColor: '#1e1e1e' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    showCount: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', color: '#bb86fc', fontWeight: 'bold' },
    roleAttended: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    rolePerformed: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    roleWorked: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    geoSubCount: { fontSize: '11px', color: '#666', fontWeight: 'normal' },
    emptyTab: { color: '#666', fontSize: '14px', fontStyle: 'italic', padding: '24px 0' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    errorMsg: { textAlign: 'center', color: '#e57373', fontSize: '16px', padding: '60px 0' },
};

export default MyShows;
